import { useEffect, useRef, useState } from "react";
import type { Product } from "@shared/events";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";
import { labelFontRem } from "./cart";
import { paletteBg, paletteInk } from "./palette";

/**
 * The product grid is the home screen (F-401). One tap = one unit, no confirmation, instant feedback.
 * Sorting, filtering of archived products, and stock come from the folded ledger state.
 */

export function sortedSellableProducts(products: Record<string, Product>): Product[] {
  return Object.values(products)
    .filter((p) => p.archivedAt === null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
}

/** Remaining stock after what is already in the cart, or null when the product is not tracked. */
export function remainingStock(
  product: Product,
  stock: Record<string, number>,
  cartUnits: Record<string, number>,
): number | null {
  if (!product.trackStock) return null;
  return (stock[product.id] ?? 0) - (cartUnits[product.id] ?? 0);
}

function ProductTile(props: {
  product: Product;
  currency: CurrencyCode;
  remaining: number | null;
  onPick: (p: Product) => void;
}) {
  const { product, remaining } = props;
  const soldOut = remaining !== null && remaining <= 0;
  const [bump, setBump] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const badge = remaining === null ? null : soldOut ? "Sold out" : String(remaining);
  const name = `${product.label}, ${formatMinor(product.priceMinor, props.currency)}${soldOut ? ", sold out" : ""}`;

  return (
    <button
      type="button"
      className={`sell__tile${bump ? " sell__tile--bump" : ""}${soldOut ? " sell__tile--out" : ""}`}
      style={{ background: paletteBg(product.color), color: paletteInk(product.color) }}
      aria-label={name}
      data-testid={`tile-${product.id}`}
      onClick={() => {
        setBump((n) => n + 1);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setBump(0), 200);
        props.onPick(product);
      }}
    >
      {badge ? (
        <span className={`sell__tile-badge${soldOut ? " sell__tile-badge--out" : ""}`} aria-hidden="true">
          {badge}
        </span>
      ) : null}
      <span className="sell__tile-label" style={{ fontSize: `${labelFontRem(product.label)}rem` }}>
        {product.label}
      </span>
      <span className="sell__tile-price" aria-hidden="true">
        {formatMinor(product.priceMinor, props.currency)}
      </span>
    </button>
  );
}

export function ProductGrid(props: {
  products: Product[];
  stock: Record<string, number>;
  cartUnits: Record<string, number>;
  currency: CurrencyCode;
  onPick: (p: Product) => void;
}) {
  if (props.products.length === 0) {
    return (
      <div className="sell__grid-scroll">
        <p className="sell__empty">No products yet. Add them in Setup.</p>
      </div>
    );
  }
  return (
    <div className="sell__grid-scroll">
      <div className="sell__grid" role="group" aria-label="Products">
        {props.products.map((p) => (
          <ProductTile
            key={p.id}
            product={p}
            currency={props.currency}
            remaining={remainingStock(p, props.stock, props.cartUnits)}
            onPick={props.onPick}
          />
        ))}
      </div>
    </div>
  );
}
